import { Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import Login from "./components/Login";
import ProtectedRoute from "./middleware/ProtectedRoute";
import ForgotPassword from "./components/ForgotPassword";
import SuperadminDashboard from "./components/Superadmin/SuperadminDashboard";
import AllOrgs from "./components/Superadmin/AllOrgs";
import CreateOrg from "./components/Superadmin/CreateOrg";
import EditOrg from "./components/Superadmin/EditOrg";
import EditAdmin from "./components/Superadmin/EditAdmin";
import AdminDashboard from "./components/Admin/AdminDashboard";
import OrgDetails from "./components/Admin/OrgDetails";
import AllOrgsUsers from "./components/Admin/AllOrgUsers";
import CreateUser from "./components/Admin/CreateUser";
import LeadStatus from "./components/Superadmin/LeadStatus";
import CreateTeam from "./components/Admin/CreateTeam";
import Teams from "./components/Admin/Teams";
import Projects from "./components/Admin/Projects";
import CreateProject from "./components/Admin/CreateProject";
import ViewProject from "./components/Admin/ViewProject";
import UserDashboard from "./components/Users/UserDashboard";
import Dashboard from "./components/Users/Dashboard";
import AddLead from "./components/Users/AddLead";
import ViewHistory from "./components/Users/ViewHistory";
import EditLead from "./components/Users/EditLead";
import CreateStructure from "./components/CreateStructure";
import EditStructure from "./components/EditStructure";
import SubscriptionModal from "./components/Superadmin/SubscriptionModal";
import { useEffect } from "react";
import InventoryList from "./components/InventoryList";
import AddInventory from "./components/AddInventory";
import EditProject from "./components/Admin/EditProject";
import EditTeam from "./components/Admin/EditTeam";
import EditInventory from "./components/EditInventory";
import LeadDashboard from "./components/Admin/LeadDashboard";
import LeadStatusForProject from "./components/Admin/LeadStatusForProject";
import UserInvites from "./components/Admin/UserInvites";
import Partners from "./components/Partners";
import AddPartner from "./components/AddPartner";
import PartnerDashboard from "./components/Users/Partners/PartnerDashboard";
import AcceptInvite from "./components/AcceptInvite";
import RemovedUsers from "./components/Admin/RemovedUsers";
import Reminders from "./components/Reminders";
import AddReminder from "./components/AddReminder";
import EditReminder from "./components/EditReminder";
import LeadTable from "./components/Users/LeadTable";
import TodayTasks from "./components/TodayTasks";
import LeadProjectDashboard from "./components/Users/LeadProjectDashboard";
import NotFound from "./components/NotFound";
import { jwtDecode } from "jwt-decode";
import {
  OrganizationProvider,
  useOrganization,
} from "./utils/OrganizationContext";
import { Backdrop, CircularProgress } from "@mui/material";
import FacebookIntegration from "./components/Admin/FacebookIntegration";
import Checkout, { PaymentResult } from "./components/Checkout";

const USER_ROLES = [
  "VT1",
  "VT2",
  "TELECALLER",
  "SALESPERSON",
  "PROJECT_HEAD",
  "MARKETER",
];

function DashboardRoutes() {
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const { role, isInitializing, isSwitching } = useOrganization();
  if (!storedUser?.accessToken) {
    return <Navigate to="/" replace />;
  }
  if (isSwitching || isInitializing) {
    return (
      <Backdrop
        open
        sx={{
          zIndex: 9999,
          color: "#fff",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }
  if (!role) {
    return <Navigate to="/not-found" replace />;
  }

  // let role;
  // try {
  //   const decoded = jwtDecode(storedUser.accessToken);
  //   console.log(decoded);
  //   role = decoded.userRole[0];
  // } catch (e) {
  //   return <Navigate to="/" replace />;
  // }

  // ---------- ADMIN ----------
  if (role === "ADMIN") {
    return (
      <Routes>
        <Route element={<AdminDashboard />}>
          <Route index element={<TodayTasks />} />
          <Route path="org-details" element={<OrgDetails />} />
          <Route path="users" element={<AllOrgsUsers />} />
          <Route path="create-user" element={<CreateUser />} />
          <Route path=":orgUserId" element={<CreateUser />} />
          <Route path="teams" element={<Teams />} />
          <Route path="create-team" element={<CreateTeam />} />
          <Route path="edit-team/:teamId" element={<EditTeam />} />
          <Route path="projects" element={<Projects />} />
          <Route path="create-project" element={<CreateProject />} />
          <Route path="edit-project/:projectId" element={<EditProject />} />
          <Route path="projects/:projectId" element={<ViewProject />} />
          <Route
            path="projects/lead-status/:projectId"
            element={<LeadStatusForProject />}
          />
          <Route path="inventory" element={<InventoryList />} />
          <Route path="inventory/add-inventory" element={<AddInventory />} />
          <Route
            path="inventory/edit-inventory/:inventoryId"
            element={<EditInventory />}
          />
          <Route path="create-structure" element={<CreateStructure />} />
          <Route path="edit-structure/:projectId" element={<EditStructure />} />
          <Route path="leads" element={<LeadDashboard />} />
          <Route path="add-lead" element={<AddLead />} />
          <Route path="add-lead/:projectId" element={<AddLead />} />
          <Route path="lead/:leadId" element={<ViewHistory />} />
          <Route path="edit-lead/:leadId" element={<EditLead />} />
          <Route path="invites" element={<UserInvites />} />
          <Route path="partners" element={<Partners />} />
          <Route path="add-partner" element={<AddPartner />} />
          <Route path="partner/:partnerId" element={<AddPartner />} />
          <Route path="past-members" element={<RemovedUsers />} />
          <Route path="reminder" element={<Reminders />} />
          <Route path="reminder/add-reminder" element={<AddReminder />} />
          <Route path="partner/leads" element={<PartnerDashboard />} />
          <Route path="reminder/edit/:reminderId" element={<EditReminder />} />
          <Route
            path="integrations/facebook"
            element={<FacebookIntegration />}
          />
          <Route path="checkout" element={<Checkout />} />
          <Route path="payment/result" element={<PaymentResult />} />
        </Route>
      </Routes>
    );
  }

  // ---------- NON-ADMIN ----------
  if (USER_ROLES.includes(role)) {
    return (
      <Routes>
        <Route element={<UserDashboard />}>
          <Route index element={<Dashboard />} />
          <Route path="partner/leads" element={<PartnerDashboard />} />
          <Route path="reminder" element={<Reminders />} />
          <Route path="reminder/add-reminder" element={<AddReminder />} />
          <Route path="reminder/edit/:reminderId" element={<EditReminder />} />
          <Route path="projects/:projectId" element={<ViewProject />} />
          <Route path="payment/result" element={<PaymentResult />} />

          {["TELECALLER", "SALESPERSON", "PROJECT_HEAD"].includes(role) && (
            <>
              <Route path="add-lead" element={<AddLead />} />
              <Route path="add-lead/:projectId" element={<AddLead />} />
              <Route path="edit-lead/:leadId" element={<EditLead />} />
              <Route path="lead/:leadId" element={<ViewHistory />} />
              <Route path="inventory" element={<InventoryList />} />
              <Route
                path="inventory/add-inventory"
                element={<AddInventory />}
              />
              <Route
                path="inventory/edit-inventory/:inventoryId"
                element={<EditInventory />}
              />
              <Route
                path="project-dashboard"
                element={<LeadProjectDashboard />}
              />
              <Route path="projects" element={<Projects />} />
            </>
          )}

          {role === "PROJECT_HEAD" && (
            <>
              <Route path="create-structure" element={<CreateStructure />} />
              <Route
                path="edit-structure/:projectId"
                element={<EditStructure />}
              />
            </>
          )}

          {["PROJECT_HEAD", "SALESPERSON"].includes(role) && (
            <>
              <Route path="partners" element={<Partners />} />
              <Route path="add-partner" element={<AddPartner />} />
              <Route path="partner/:partnerId" element={<AddPartner />} />
            </>
          )}
        </Route>
      </Routes>
    );
  }

  return <Navigate to="/not-found" replace />;
}

function App() {
  useEffect(() => {
    const wheelHandler = (e) => {
      if (document.activeElement.matches("input[type=number]")) {
        e.preventDefault();
      }
    };

    const keyHandler = (e) => {
      if (
        document.activeElement.matches("input[type=number]") &&
        (e.key === "ArrowUp" || e.key === "ArrowDown")
      ) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", wheelHandler, { passive: false });
    window.addEventListener("keydown", keyHandler);

    return () => {
      window.removeEventListener("wheel", wheelHandler);
      window.removeEventListener("keydown", keyHandler);
    };
  }, []);
  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/user-onboarding" element={<ForgotPassword />} />
        <Route path="/register-org" element={<CreateOrg />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />

        <Route
          element={
            <OrganizationProvider>
              <ProtectedRoute allowedRoles={["SUPERADMIN"]} />
            </OrganizationProvider>
          }
        >
          <Route path="/superadmin-dashboard" element={<SuperadminDashboard />}>
            <Route path="/superadmin-dashboard" element={<AllOrgs />} />
            <Route
              path="/superadmin-dashboard/create-org"
              element={<CreateOrg />}
            />
            <Route path="/superadmin-dashboard/:orgId" element={<EditOrg />} />
            <Route
              path="/superadmin-dashboard/:orgId/admin"
              element={<EditAdmin />}
            />
            <Route
              path="/superadmin-dashboard/meta-status"
              element={<LeadStatus />}
            />
            <Route
              path="/superadmin-dashboard/features"
              element={<SubscriptionModal />}
            />
          </Route>
        </Route>

        <Route
          element={
            <OrganizationProvider>
              <ProtectedRoute
                allowedRoles={[
                  "ADMIN",
                  "VT1",
                  "VT2",
                  "TELECALLER",
                  "SALESPERSON",
                  "PROJECT_HEAD",
                  "MARKETER",
                ]}
              />
            </OrganizationProvider>
          }
        >
          <Route path="/dashboard/*" element={<DashboardRoutes />} />
        </Route>

        <Route
          element={
            <OrganizationProvider>
              <ProtectedRoute allowedRoles={["PARTNER"]} />
            </OrganizationProvider>
          }
        >
          <Route path="/partner" element={<PartnerDashboard />}></Route>
          <Route path="/partner/:leadId" element={<ViewHistory />} />
        </Route>

        <Route path="*" element={<NotFound />} />
        <Route path="/not-found" element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default App;
